import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Shield, RefreshCcw } from 'lucide-react';

const TermsAndPrivacy = () => {
  return (
    <div className="min-h-screen bg-[#1a1a1a] p-6">
      <div className="max-w-4xl mx-auto">
        {/* Back Link */}
        <Link to="/" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-orange-500/20 rounded-lg">
              <FileText className="w-8 h-8 text-orange-500" />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-orange-500 mb-1">LEGAL</div>
              <h1 
                className="text-3xl md:text-4xl font-bold text-orange-500 tracking-tight uppercase"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Terms, Privacy & Refund Policy
              </h1>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-8">
          
          {/* Terms of Service */}
          <div className="bg-black/70 backdrop-blur-sm border border-zinc-800 rounded-lg p-8">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-6 h-6 text-orange-500" />
              <h2 className="text-2xl font-bold text-white">Terms of Service</h2>
            </div>
            
            <div className="prose prose-invert max-w-none text-zinc-300">
              <p className="text-sm text-zinc-500 mb-4">
                <strong>Last Updated:</strong> 10 February 2026<br />
                <strong>Website:</strong> WWW.CNC-Helpdesk.com<br />
                <strong>Company:</strong> CNC-Helpdesk (Andre Booysen)
              </p>

              <h3 className="text-lg font-semibold text-orange-400 mt-6 mb-3">1. Acceptance of Terms</h3>
              <p>By accessing or using CNC-Helpdesk.com ("the Service"), you agree to be bound by these Terms of Service. If you do not agree, you may not use the Service.</p>
              <p>CNC-Helpdesk provides educational software tools, including calculators, troubleshooting assistants, machine setup guidance, and instructional content for CNC machinery. The Service does not operate, control, or automate hardware.</p>

              <h3 className="text-lg font-semibold text-orange-400 mt-6 mb-3">2. Eligibility</h3>
              <p>You must be at least 18 years old to use this Service. By using the Service, you confirm that you meet this requirement.</p>

              <h3 className="text-lg font-semibold text-orange-400 mt-6 mb-3">3. Nature of the Service</h3>
              <p>CNC-Helpdesk is a subscription-based SaaS platform offering:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>CNC router guides and calculators</li>
                <li>3D printing setup and troubleshooting</li>
                <li>Laser cutting educational tools</li>
                <li>Machine setup workflows</li>
                <li>Step-by-step instructional content</li>
              </ul>
              
              <p className="mt-4"><strong>The Service is informational only.</strong> The platform does not:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Control CNC equipment</li>
                <li>Trigger machine movements</li>
                <li>Modify hardware firmware</li>
                <li>Replace professional training</li>
              </ul>
              <p className="mt-2">You assume full responsibility for safely operating your machines.</p>

              <h3 className="text-lg font-semibold text-orange-400 mt-6 mb-3">4. User Accounts</h3>
              <p>To access premium features, users must create an account. You agree to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Provide accurate information</li>
                <li>Maintain account security</li>
                <li>Not share login credentials</li>
              </ul>
              <p className="mt-2">You are responsible for all activity that occurs under your account.</p>

              <h3 className="text-lg font-semibold text-orange-400 mt-6 mb-3">5. Subscriptions & Billing</h3>
              <p>Premium access is offered on a subscription basis. By subscribing, you authorize CNC-Helpdesk (via LemonSqueezy) to charge your payment method on a recurring basis.</p>
              <p>Subscription features and pricing are listed on the Website. Your subscription will renew automatically unless cancelled before the next billing cycle.</p>

              <h3 className="text-lg font-semibold text-orange-400 mt-6 mb-3">6. Cancellation</h3>
              <p>You may cancel your subscription at any time through your user account or via LemonSqueezy's customer portal. Upon cancellation, you will retain access for the remainder of the billing period.</p>

              <h3 className="text-lg font-semibold text-orange-400 mt-6 mb-3">7. Prohibited Use</h3>
              <p>You agree not to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Reverse engineer or copy the platform</li>
                <li>Use the Service to develop competing products</li>
                <li>Attempt unauthorized access to systems</li>
                <li>Misuse automated tools or APIs</li>
                <li>Upload harmful content</li>
                <li>Violate applicable laws</li>
              </ul>
              <p className="mt-2">Any misuse may result in immediate termination.</p>

              <h3 className="text-lg font-semibold text-orange-400 mt-6 mb-3">8. Disclaimer of Warranties</h3>
              <p>The Service is provided "as is" and "as available." CNC-Helpdesk does not guarantee:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Uninterrupted operation</li>
                <li>Error-free content</li>
                <li>Accuracy of calculations or settings</li>
                <li>Outcomes of machine operation</li>
              </ul>
              <p className="mt-2">Use of CNC equipment carries inherent risk. You acknowledge that you are solely responsible for safe machine usage.</p>

              <h3 className="text-lg font-semibold text-orange-400 mt-6 mb-3">9. Limitation of Liability</h3>
              <p>To the maximum extent permitted by law, CNC-Helpdesk is not liable for:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Injury or damage caused by CNC machine use</li>
                <li>Loss of data</li>
                <li>Loss of profits</li>
                <li>Business interruptions</li>
                <li>Reliance on instructional content</li>
                <li>Third-party actions</li>
              </ul>
              <p className="mt-2">Your exclusive remedy is discontinuing use of the Service.</p>

              <h3 className="text-lg font-semibold text-orange-400 mt-6 mb-3">10. Modifications</h3>
              <p>CNC-Helpdesk may update these Terms at any time. Continued use constitutes acceptance of any changes.</p>

              <h3 className="text-lg font-semibold text-orange-400 mt-6 mb-3">11. Contact</h3>
              <p>For questions, please contact: <a href="mailto:Andre@cnc-helpdesk.com" className="text-orange-500 hover:underline">Andre@cnc-helpdesk.com</a></p>
            </div>
          </div>

          {/* Privacy Policy */}
          <div className="bg-black/70 backdrop-blur-sm border border-zinc-800 rounded-lg p-8">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-6 h-6 text-orange-500" />
              <h2 className="text-2xl font-bold text-white">Privacy Policy</h2>
            </div>
            
            <div className="prose prose-invert max-w-none text-zinc-300">
              <p className="text-sm text-zinc-500 mb-4">
                <strong>Last Updated:</strong> 10 February 2026
              </p>

              <h3 className="text-lg font-semibold text-orange-400 mt-6 mb-3">Information We Collect</h3>
              <p>We collect information you provide directly, including:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Account information (name, email, password)</li>
                <li>Machine profiles and settings you save</li>
                <li>Usage data and preferences</li>
                <li>Communication with our support team</li>
              </ul>

              <h3 className="text-lg font-semibold text-orange-400 mt-6 mb-3">How We Use Your Information</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>To provide and improve our services</li>
                <li>To personalize your experience</li>
                <li>To communicate with you about your account</li>
                <li>To send important updates and notifications</li>
                <li>To process payments (via LemonSqueezy)</li>
              </ul>

              <h3 className="text-lg font-semibold text-orange-400 mt-6 mb-3">Data Security</h3>
              <p>We implement appropriate security measures to protect your personal information. However, no method of transmission over the Internet is 100% secure.</p>

              <h3 className="text-lg font-semibold text-orange-400 mt-6 mb-3">Third-Party Services</h3>
              <p>We use third-party services including:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>LemonSqueezy for payment processing</li>
                <li>OpenAI for AI assistance features</li>
                <li>Resend for email notifications</li>
              </ul>

              <h3 className="text-lg font-semibold text-orange-400 mt-6 mb-3">Your Rights</h3>
              <p>You have the right to access, correct, or delete your personal information. Contact us at <a href="mailto:Andre@cnc-helpdesk.com" className="text-orange-500 hover:underline">Andre@cnc-helpdesk.com</a> for any privacy-related requests.</p>
            </div>
          </div>

          {/* Refund Policy */}
          <div className="bg-black/70 backdrop-blur-sm border border-zinc-800 rounded-lg p-8">
            <div className="flex items-center gap-3 mb-6">
              <RefreshCcw className="w-6 h-6 text-orange-500" />
              <h2 className="text-2xl font-bold text-white">Refund Policy</h2>
            </div>
            
            <div className="prose prose-invert max-w-none text-zinc-300">
              <p className="text-sm text-zinc-500 mb-4">
                <strong>Last Updated:</strong> 10 February 2026
              </p>

              <h3 className="text-lg font-semibold text-orange-400 mt-6 mb-3">Subscription Refunds</h3>
              <p>We want you to be satisfied with CNC-Helpdesk. If you're not happy with your subscription:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Monthly subscriptions:</strong> Refunds may be issued within 7 days of initial purchase if you haven't extensively used the service.</li>
                <li><strong>Annual subscriptions:</strong> Refunds may be issued within 14 days of initial purchase if you haven't extensively used the service.</li>
              </ul>

              <h3 className="text-lg font-semibold text-orange-400 mt-6 mb-3">How to Request a Refund</h3>
              <p>To request a refund, please contact us at <a href="mailto:Andre@cnc-helpdesk.com" className="text-orange-500 hover:underline">Andre@cnc-helpdesk.com</a> with:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Your account email address</li>
                <li>Date of purchase</li>
                <li>Reason for the refund request</li>
              </ul>

              <h3 className="text-lg font-semibold text-orange-400 mt-6 mb-3">Non-Refundable Situations</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Subscriptions cancelled after the refund period</li>
                <li>Accounts that have violated our Terms of Service</li>
                <li>Partial month/year refunds after cancellation</li>
              </ul>

              <h3 className="text-lg font-semibold text-orange-400 mt-6 mb-3">Cancellation vs Refund</h3>
              <p>Cancelling your subscription is different from requesting a refund. When you cancel, you retain access until the end of your current billing period but will not be charged again.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndPrivacy;
